import React, { useCallback, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import type { GooglePlacesAddress } from '@sweetgreen/garnish';
import { Form } from '@sweetgreen/garnish';
import {
  TextField,
  theme,
  useNoticeBannersStackContext,
  VStack,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import { useAddressesTelemetry } from './AddressForm.telemetry';
import type { AddressField, FormProps } from './AddressForm.types';
import {
  AddressType,
  DeliveryNotes,
  DeliveryPreference,
  Footer,
  Header,
} from './components/AddressForm.components';
import { AddressFormPredictions } from './components/AddressForm.components.predictions';
import { useDuplicateCustomerAddressValidation } from './hooks';
import { useAddressFormState } from './hooks';

export const AddressForm = (props: FormProps) => {
  const {
    address,
    visible,
    editing,
    loading,
    deleting,
    disableAddress,
    noHeader,
    submitCta,
    editingAddressId,
    onDelete,
    onEdit,
    onSave,
  } = props;

  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  // ─── State ───────────────────────────────────────────────────────────

  const [placeId, setPlaceId] = useState('');
  const form = useAddressFormState(address);
  const formValues = form.watch();
  const { addressType, name, notes, zipCode } = formValues;

  // ─── Form Validation ─────────────────────────────────────────────────────

  const isFormChanged = form.formState.isDirty;
  const isCustomNameTouched = form.formState.dirtyFields.name;
  const isAddressTouched = form.formState.dirtyFields.street;
  const isValidStreet =
    Boolean(placeId) || (!isAddressTouched && Boolean(address?.id));
  const hasValidAddressName = addressType !== 'custom' || Boolean(name);

  // ─── Address Duplication Checks ──────────────────────────────────────────

  const { checkIfDuplicateAddressByStreet, checkIfDuplicateAddressByName } =
    useDuplicateCustomerAddressValidation(editingAddressId ?? address?.id);
  const isPlaceIdDuplicate = checkIfDuplicateAddressByStreet(formValues);
  const placeIdDuplicateNotice = t('account.address.street-duplicate');
  const isNameDuplicate =
    isCustomNameTouched && checkIfDuplicateAddressByName(name);
  const isNameMissing = isCustomNameTouched && !name;
  const isNameInvalid = isNameDuplicate ?? isNameMissing;
  const isZipCodeMissing = !zipCode;
  const customNameNotice = useCustomNameNotice(isNameDuplicate, isNameMissing);

  // ─── Telemetry ──────────────────────────────────────────────────

  const { monitoredOnSave, monitoredOnEdit, monitoredOnDelete } =
    useAddressesTelemetry({
      editing,
      onDelete,
      onEdit,
      onSave,
    });

  // ─── Callbacks ──────────────────────────────────────────────────

  const updateField = useCallback(
    (field: AddressField) => (value: string | number | undefined) => {
      form.setValue(field, value, { shouldDirty: true });
    },
    [form],
  );

  const submitForm = useCallback(() => {
    (() => {
      if (isPlaceIdDuplicate) {
        addNoticeBanner({ text: placeIdDuplicateNotice, palette: 'caution' });

        return;
      }

      if (editing) {
        monitoredOnEdit(formValues);

        return;
      }

      monitoredOnSave(formValues);
    })();
  }, [
    editing,
    formValues,
    isPlaceIdDuplicate,
    placeIdDuplicateNotice,
    addNoticeBanner,
    monitoredOnEdit,
    monitoredOnSave,
  ]);

  // ─── Submit Control ─────────────────────────────────────────────

  const preventSubmittingForUnchangedForm = Boolean(address) && !isFormChanged;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- @REVIEW
  const preventSubmittingForInvalidFields = isNameInvalid || isZipCodeMissing;
  const canSubmitAfterValidation =
    Boolean(addressType) &&
    isValidStreet &&
    hasValidAddressName &&
    !preventSubmittingForUnchangedForm &&
    !preventSubmittingForInvalidFields;

  return (
    <>
      {noHeader ? (
        <View style={styles.headerPadding} />
      ) : (
        <View style={styles.header}>
          <Header editing={editing} />
        </View>
      )}

      <View style={styles.body} testID="address-form.content">
        <AddressFormPredictions
          placeId={placeId}
          address={address as GooglePlacesAddress}
          visible={visible}
          disabled={disableAddress}
          setPlaceId={setPlaceId}
          setValue={form.setValue}
        />

        <VStack customSpacing={10} style={styles.content}>
          <Form.TextField
            name="secondaryStreet"
            control={form.control}
            testID="account.address-form.secondary-street"
            accessibilityLabel={t('account.addresses.secondary-street')}
            label={t('account.addresses.secondary-street')}
          />
          <DeliveryPreference form={formValues} updateField={updateField} />
          <DeliveryNotes form={form} notes={notes} maxLength={128} />
          <AddressType
            addressType={addressType}
            updateField={updateField}
            setAddressType={updateField('addressType')}
          />
          {addressType === 'custom' && (
            <TextField
              testID="account.address-form.custom-name"
              maxLength={20}
              value={name}
              accessibilityLabel={t('account.addresses.custom-name')}
              accessibilityHint={t('account.addresses.custom-name')}
              label={t('account.addresses.custom-name')}
              onChangeText={updateField('name')}
              invalid={isNameInvalid}
              notice={customNameNotice}
            />
          )}
        </VStack>
      </View>

      <Footer
        submitCta={submitCta}
        editing={editing}
        deleting={deleting}
        loading={loading}
        canSubmit={canSubmitAfterValidation}
        submitForm={submitForm}
        onDelete={monitoredOnDelete}
      />
    </>
  );
};

const useCustomNameNotice = (
  isNameDuplicate?: boolean,
  isNameMissing?: boolean,
) => {
  const { t } = useLocalizationContext();

  if (isNameDuplicate) {
    return t('account.address.name-duplicate');
  }

  if (isNameMissing) {
    return t('account.addresses.required');
  }

  return '';
};

const styles = StyleSheet.create({
  headerPadding: {
    paddingTop: theme.spacing.large,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  body: {
    paddingBottom: theme.spacing.large,
    paddingHorizontal: theme.spacing.large,
  },
  content: {
    width: '100%',
  },
});
